import { CUSTOMER } from '@lumapps/customer/keys';
import { GLOBAL } from '@lumapps/translations/keys';
import { getWysiwygToolbar } from '@lumapps/wrex-legacy-froala/constants';

import { CUSTOMER_SETTINGS } from './keys';

/**
 * Translation keys for WYSIWYG toolbar items, derived dynamically from the Froala default toolbar.
 * Exported for reuse in packages that share HTMLContentEditorFieldset (e.g. instance-settings).
 * Deduplication via Set handles repeated separators ('|', '-').
 */
export const ADVANCED_SETTINGS_WYSIWYG_TOOLBAR_ITEM_KEYS = [
    ...new Set(Object.values(getWysiwygToolbar()).map((tool) => tool.translationKey)),
];

/**
 * Translation keys shared by all fieldsets used across both the customer and instance Advanced Settings pages:
 * AvailableLanguagesFieldset, ContentConfigurationFieldset, HTMLContentEditorFieldset, FeaturesFieldset.
 *
 * /!\ When adding a new translateKey() call to one of those fieldsets, add the key here first.
 *
 * Note: dynamically-generated keys (e.g. WYSIWYG toolbar items) cannot be listed here as named
 * properties — they are included via CUSTOMER_ADVANCED_SETTINGS_ALL_PAGE_KEYS instead.
 */
export const COMMON_ADVANCED_SETTINGS_PAGE_KEYS = {
    // From AvailableLanguagesFieldset
    LANGUAGES: GLOBAL.LANGUAGES,
    YES: GLOBAL.YES,
    NO: GLOBAL.NO,
    BROWSER_TRANSLATION: CUSTOMER_SETTINGS.BROWSER_TRANSLATION,
    AVAILABLE_LANGS: CUSTOMER_SETTINGS.AVAILABLE_LANGS,
    FALLBACK_LANGS: CUSTOMER_SETTINGS.FALLBACK_LANGS,

    // From ContentConfigurationFieldset
    CONTENT: GLOBAL.CONTENT,
    KEYWORD: GLOBAL.KEYWORD,
    AUTHOR: GLOBAL.AUTHOR,
    TAGS: GLOBAL.TAGS,
    PUBLICATION_DATE: GLOBAL.PUBLICATION_DATE,
    TITLE: GLOBAL.TITLE,
    EXCERPT: GLOBAL.EXCERPT,
    METADATA: GLOBAL.METADATA,
    DATE: GLOBAL.DATE,
    THUMBNAIL: GLOBAL.THUMBNAIL,
    NAME: GLOBAL.NAME,
    DESCRIPTION: GLOBAL.DESCRIPTION,
    LAST_UPDATED_DATE: GLOBAL.LAST_UPDATED_DATE,
    COMMUNITY_FILTERS: CUSTOMER_SETTINGS.COMMUNITY_FILTERS,
    CONTENT_LIST_LINK_OPEN_NEW_TAB: CUSTOMER_SETTINGS.CONTENT_LIST_LINK_OPEN_NEW_TAB,
    CONTENT_LIST_EXCERPT: CUSTOMER_SETTINGS.CONTENT_LIST_EXCERPT,
    CONTENT_FIELDS: CUSTOMER_SETTINGS.CONTENT_FIELDS,
    WIDGET_TYPE_CONTENT_LIST_SOCIAL: CUSTOMER_SETTINGS.WIDGET_TYPE_CONTENT_LIST_SOCIAL,
    CONTENT_FILTERS: CUSTOMER_SETTINGS.CONTENT_FILTERS,
    WIDGET_TYPE_CONTENT_FILTER_DIRECTORY_TAGS: CUSTOMER_SETTINGS.WIDGET_TYPE_CONTENT_FILTER_DIRECTORY_TAGS,
    DIRECTORY_FILTERS: CUSTOMER_SETTINGS.DIRECTORY_FILTERS,
    FILE_FIELDS: CUSTOMER_SETTINGS.FILE_FIELDS,
    META_FIELDS: CUSTOMER_SETTINGS.META_FIELDS,
    META_SOCIAL_FIELDS: CUSTOMER_SETTINGS.META_SOCIAL_FIELDS,
    WIDGET_TYPE_META_SOCIAL_LIKE: CUSTOMER_SETTINGS.WIDGET_TYPE_META_SOCIAL_LIKE,
    WIDGET_TYPE_META_SOCIAL_COMMENT: CUSTOMER_SETTINGS.WIDGET_TYPE_META_SOCIAL_COMMENT,
    WIDGET_TYPE_META_SOCIAL_FOLLOW: CUSTOMER_SETTINGS.WIDGET_TYPE_META_SOCIAL_FOLLOW,
    POST_FIELDS: CUSTOMER_SETTINGS.POST_FIELDS,
    WIDGET_TYPE_POST_LIST_SHARED_IN_COMMUNITIES: CUSTOMER_SETTINGS.WIDGET_TYPE_POST_LIST_SHARED_IN_COMMUNITIES,
    WIDGET_TYPE_POST_LIST_DATES: CUSTOMER_SETTINGS.WIDGET_TYPE_POST_LIST_DATES,
    WIDGET_TYPE_POST_LIST_ATTACHMENTS: CUSTOMER_SETTINGS.WIDGET_TYPE_POST_LIST_ATTACHMENTS,
    COMMENTS: CUSTOMER_SETTINGS.COMMENTS,
    SOCIAL_SETTINGS: CUSTOMER_SETTINGS.SOCIAL_SETTINGS,
    SOCIAL_SETTINGS_MULTIPOSTING_ALLOWED: CUSTOMER_SETTINGS.SOCIAL_SETTINGS_MULTIPOSTING_ALLOWED,
    USER_BLOCK_DEFAULT_FIELD: CUSTOMER_SETTINGS.USER_BLOCK_DEFAULT_FIELD,
    USER_BLOCK_USER_DIRECTORY_FIELD: CUSTOMER_SETTINGS.USER_BLOCK_USER_DIRECTORY_FIELD,

    // From HTMLContentEditorFieldset
    WYSIWYG: CUSTOMER_SETTINGS.WYSIWYG,
    WYSIWYG_PASTEPLAIN: CUSTOMER_SETTINGS.WYSIWYG_PASTEPLAIN,
    WYSIWYG_TOOLBAR: CUSTOMER_SETTINGS.WYSIWYG_TOOLBAR,

    // From FeaturesFieldset
    BETA_FEATURES: CUSTOMER_SETTINGS.BETA_FEATURES,
    BLOCK_XSS_HTML_TAGS: CUSTOMER_SETTINGS.BLOCK_XSS_HTML_TAGS,
    PLATFORM_SETTINGS_HTML_WHITELIST_PLATFORM: CUSTOMER_SETTINGS.PLATFORM_SETTINGS_HTML_WHITELIST_PLATFORM,
    PLATFORM_SETTINGS_HTML_WHITELIST_SITE: CUSTOMER_SETTINGS.PLATFORM_SETTINGS_HTML_WHITELIST_SITE,
    PLATFORM_SETTINGS_HTML_WHITELIST_ALL: CUSTOMER_SETTINGS.PLATFORM_SETTINGS_HTML_WHITELIST_ALL,
} as const;

/**
 * Named translation keys used directly with translateKey() in the customer's AdvancedSettingsPage.
 * Adds the customer-specific page header keys on top of the shared fieldset keys.
 *
 * /!\ When adding a new translateKey() call to AdvancedSettingsPage (customer), add the key here first.
 */
export const CUSTOMER_ADVANCED_SETTINGS_PAGE_KEYS = {
    // From AdvancedSettingsPage UI (customer-specific)
    CUSTOMER_SETTINGS: CUSTOMER.CUSTOMER_SETTINGS,
    ADVANCED_SETTINGS: CUSTOMER_SETTINGS.ADVANCED_SETTINGS,
    ...COMMON_ADVANCED_SETTINGS_PAGE_KEYS,
} as const;

/**
 * Complete set of all translation keys used across the customer Advanced Settings page (for Spotlight / pageKeys).
 * Combines the statically-named keys with the dynamically-generated WYSIWYG toolbar item keys.
 */
export const CUSTOMER_ADVANCED_SETTINGS_ALL_PAGE_KEYS = [
    ...Object.values(CUSTOMER_ADVANCED_SETTINGS_PAGE_KEYS),
    ...ADVANCED_SETTINGS_WYSIWYG_TOOLBAR_ITEM_KEYS,
];
